import React, { useState, useEffect, useCallback } from 'react';
import { X, Copy, Check, Tv, Clock } from 'lucide-react';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';

import { RubyGemMark } from '../RubyGemMark';
interface Props {
  tenantId: string;
  onClose: () => void;
  onCreated: (display: { displayId: string; provisionCode: string }) => void;
}

interface TenantLocation {
  locationId: string;
  name: string;
  status: string;
}

const ProvisionDisplayModal: React.FC<Props> = ({ tenantId, onClose, onCreated }) => {
  const [locations, setLocations] = useState<TenantLocation[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);

  const [name, setName] = useState('');
  const [locationId, setLocationId] = useState('');
  const [description, setDescription] = useState('');
  const [resolution, setResolution] = useState<'1080p' | '4k'>('1080p');
  const [orientation, setOrientation] = useState<'landscape' | 'portrait'>('landscape');

  const [provisioning, setProvisioning] = useState(false);
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const fetchLocations = useCallback(async () => {
    setLocationsLoading(true);
    try {
      const data: any = await api.locationBilling.listTenantRequests(tenantId);
      const active = (data?.requests || [])
        .filter((r: any) => r.status === 'active' && r.locationId)
        .map((r: any) => ({ locationId: r.locationId, name: r.name || r.locationId, status: r.status }));
      setLocations(active);
    } catch (err) {
      console.error('Failed to load locations:', err);
    } finally {
      setLocationsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const handleProvision = async () => {
    if (!name.trim()) {
      toastService.warning('Name is required');
      return;
    }
    if (!locationId) {
      toastService.warning('Location is required');
      return;
    }
    setProvisioning(true);
    try {
      const resp = await api.advertising.provisionDisplay({
        tenantId,
        locationId,
        name: name.trim(),
        description: description.trim() || undefined,
        resolution,
        orientation,
      });
      setGeneratedCode(resp.provisionCode);
      toastService.success('Display provisioned');
      onCreated({ displayId: resp.displayId, provisionCode: resp.provisionCode });
    } catch (err: any) {
      toastService.error('Failed to provision: ' + (err.message || 'Unknown error'));
    } finally {
      setProvisioning(false);
    }
  };

  const handleCopy = () => {
    if (!generatedCode) return;
    navigator.clipboard.writeText(generatedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-black text-white px-5 py-3 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-2">
            <Tv className="w-5 h-5 text-white" />
            <h3 className="text-base md:text-lg font-semibold">
              {generatedCode ? 'Display Provisioned' : 'New Marketing Display'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/10 text-white transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5">
          {!generatedCode ? (
            <>
              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label className="text-stone-600 text-xs font-medium mb-1 block">
                    Display Name *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Lobby TV"
                    className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-stone-800 text-sm focus:outline-none focus:border-stone-400"
                  />
                </div>

                {/* Location */}
                <div>
                  <label className="text-stone-600 text-xs font-medium mb-1 block">
                    Location *
                  </label>
                  {locationsLoading ? (
                    <div className="flex items-center gap-2 text-stone-400 text-sm py-2">
                      <RubyGemMark size={14} drawing /> Loading locations...
                    </div>
                  ) : locations.length === 0 ? (
                    <p className="text-amber-600 text-xs py-2">
                      No active locations found for this tenant.
                    </p>
                  ) : (
                    <select
                      value={locationId}
                      onChange={(e) => setLocationId(e.target.value)}
                      className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-stone-800 text-sm focus:outline-none focus:border-stone-400"
                    >
                      <option value="">— Select Location —</option>
                      {locations.map((loc) => (
                        <option key={loc.locationId} value={loc.locationId}>
                          {loc.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="text-stone-600 text-xs font-medium mb-1 block">
                    Description (optional)
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="65-inch TV in lobby above counter"
                    rows={2}
                    className="w-full bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-stone-800 text-sm focus:outline-none focus:border-stone-400 resize-none"
                  />
                </div>

                {/* Resolution */}
                <div>
                  <label className="text-stone-600 text-xs font-medium mb-1 block">Resolution</label>
                  <div className="flex gap-2">
                    {(['1080p', '4k'] as const).map((r) => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setResolution(r)}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                          resolution === r
                            ? 'bg-emerald-600 text-white border-emerald-600'
                            : 'bg-stone-50 text-stone-600 border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        {r === '1080p' ? '1080p (HD)' : '4K (UHD)'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Orientation */}
                <div>
                  <label className="text-stone-600 text-xs font-medium mb-1 block">
                    Orientation
                  </label>
                  <div className="flex gap-2">
                    {(['landscape', 'portrait'] as const).map((o) => (
                      <button
                        key={o}
                        type="button"
                        onClick={() => setOrientation(o)}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border transition-colors capitalize ${
                          orientation === o
                            ? 'bg-emerald-600 text-white border-emerald-600'
                            : 'bg-stone-50 text-stone-600 border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        {o}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 mt-6">
                <button
                  onClick={onClose}
                  disabled={provisioning}
                  className="px-3 py-2 rounded-lg text-xs font-medium text-stone-600 bg-stone-100 hover:bg-stone-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleProvision}
                  disabled={provisioning || locationsLoading}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-black text-white hover:bg-stone-800 disabled:opacity-50"
                >
                  {provisioning ? (
                    <>
                      <RubyGemMark size={14} drawing color="currentColor" /> Provisioning...
                    </>
                  ) : (
                    'Provision Display'
                  )}
                </button>
              </div>
            </>
          ) : (
            /* Success view */
            <div className="text-center py-4">
              <p className="text-stone-600 text-sm mb-1">Provisioning code:</p>
              <p className="text-stone-400 text-xs mb-4">
                Enter this code on the TV at <span className="font-mono">advertising.{window.location.hostname.includes('dev.') ? 'dev.rubytill.com' : 'rubytill.com'}</span>
              </p>

              <div className="inline-flex items-center gap-3 bg-stone-50 border-2 border-emerald-500/30 rounded-xl px-6 py-4">
                <span className="text-3xl font-mono font-bold text-emerald-600 tracking-widest select-all">
                  {generatedCode}
                </span>
                <button
                  onClick={handleCopy}
                  className="p-2 rounded-lg bg-stone-100 hover:bg-stone-200 text-stone-500 hover:text-stone-700 transition-colors"
                  title="Copy code"
                >
                  {copied ? <Check size={16} className="text-emerald-600" /> : <Copy size={16} />}
                </button>
              </div>

              <p className="text-amber-600 text-xs mt-3 flex items-center justify-center gap-1.5">
                <Clock size={12} /> Expires in 24 hours
              </p>

              <button
                onClick={onClose}
                className="mt-5 px-4 py-2 rounded-lg text-sm font-semibold bg-black text-white hover:bg-stone-800"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProvisionDisplayModal;
